import { useState, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { ApiListResponse, PersonListItem, ProductResponse } from '../types/api-responses';

interface PersonOption {
    value: string;
    label: string;
    entity_type?: string;
}

interface ProductOption {
    value: string;
    label: string;
}

interface UseOperationDataParams {
    personServiceUrl: string;
    personApiKey: string;
    contractsApiUrl: string;
    contractsApiKey: string;
}

export const useOperationData = ({ personServiceUrl, personApiKey, contractsApiUrl, contractsApiKey }: UseOperationDataParams) => {
    const [guarantors, setGuarantors] = useState<PersonOption[]>([]);
    const [products, setProducts] = useState<ProductOption[]>([]);

    const getToken = async () => {
        const session = await Auth.currentSession();
        return session.getIdToken().getJwtToken();
    };

    const fetchPersons = useCallback(async () => {
        try {
            const token = await getToken();
            const response = await fetch(`${personServiceUrl}/person/ALL`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': personApiKey
                }
            });
            if (response.ok) {
                const data: ApiListResponse<PersonListItem> = await response.json();
                const guarantorItems = data.results.map((item) => {
                    const cnpjDoc = item.documents?.find((doc) => doc.type === 'CNPJ');
                    const cpfDoc = item.documents?.find((doc) => doc.type === 'CPF');
                    const document = cnpjDoc?.number || cpfDoc?.number || '';
                    return {
                        value: item.uuid,
                        label: `${item.name} - ${document}`,
                        entity_type: item.personType === 'LEGAL_PERSON' ? 'PJ' : 'PF'
                    };
                });
                setGuarantors(guarantorItems);
            }
        } catch {
            toast.error('Erro ao carregar lista de pessoas');
        }
    }, [personServiceUrl, personApiKey]);

    const fetchProducts = useCallback(async () => {
        try {
            const token = await getToken();
            const response = await fetch(`${contractsApiUrl}/product`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': contractsApiKey
                }
            });
            if (response.ok) {
                const data = await response.json();
                const productsList = data.products || data;
                setProducts(productsList.map((product: ProductResponse) => ({
                    value: product.uuid,
                    label: product.name
                })));
            }
        } catch {
            toast.error('Erro ao carregar lista de produtos');
        }
    }, [contractsApiUrl, contractsApiKey]);

    return { guarantors, products, setProducts, fetchPersons, fetchProducts };
};
